import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { FINANCE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const defaultColSpan = {
	md: 12
};

const EditClientTransactionInvoiceModal = NiceModal.create((props) => {
	const modal = useModal();
	const {
		companyID,
		transactionID,
		invoiceNumber,
		invoiceLink,
		invoiceID,
		clientName,
		transactionDate,
		amount,
		currency,
		paymentStatus
	} = props;
	const dispatch = useDispatch();

	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess, showWarning } = useAlert();

	const handleCloseModal = useCallback(() => {
		modal.remove();
	}, []);

	const fillForm = useCallback(() => {
		return {
			companyID,
			transactionID,
			invoiceNumber,
			invoiceLink,
			invoiceID
		};
	});

	const schema = useMemo(
		() =>
			yup.object().shape({
				companyID: yup.number().nullable().required(),
				transactionID: yup.number().nullable().required(),
				invoiceNumber: yup.string().nullable().required(),
				invoiceLink: yup.string().url().nullable(),
				invoiceID: yup.string().nullable()
			}),

		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm()
	});

	const {
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;
	const isLoading = isFormLoading || isSubmitting;

	const onSubmit = async (data) => {
		try {
			const response = await axiosPrivate.post(FINANCE.UPDATE_CREDIT_TRANSACTIONS, data);
			const { status } = response;
			if (status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.CLIENT_TRANSACTIONS));
				showSuccess();
				handleCloseModal();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	return (
		<Modal fullwidth maxWidth="medium" className="modal-edit-invoice">
			<ModalTitle>
				<Typography variant="p1">editClientTransactionInvoice</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="clientName" isLoading={isLoading}>
						<StringInput value={clientName} disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="transactionDate" isLoading={isLoading}>
						<DateInput value={transactionDate} disabled enableTime />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="amount" isLoading={isLoading} required>
						<IntegerInput value={amount} unit={currency} showArrows={false} allowFloat disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="invoiceNumber" isLoading={isLoading} required>
						<StringInput name="invoiceNumber" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="invoiceID" isLoading={isLoading}>
						<StringInput name="invoiceID" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="invoiceLink" isLoading={isLoading}>
						<StringInput name="invoiceLink" />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="Cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					onClick={() => {
						if (paymentStatus === 'canceled' || paymentStatus === 'failed' || paymentStatus === 'revoked') {
							showWarning({
								header: 'alert.oops',
								message: 'invoiceUpdateFailed'
							});
							return;
						}
						handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})();
					}}
					disabled={!isDirty}
					label="save"
					loading={isLoading}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default EditClientTransactionInvoiceModal;
